import { useRef, useState, type CSSProperties } from 'react';
import { Button } from '../../components/Button';
import { Field } from '../../components/Field';
import { SeriesOccurrenceConflictBanner } from '../../components/SeriesOccurrenceConflictBanner';
import { ApiError, seriesOccurrenceConflictDetail, transferCoachSeriesTable } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { occurrenceSkipLabel } from '../../lib/coach-series';
import { formatClock } from '../../lib/time';
import type {
  SeriesOccurrenceConflictDetail,
  SeriesOccurrenceSkip,
  SeriesTableTransferResult,
  TableEmbed,
} from '../../lib/types';

interface Props {
  seriesId: number;
  /** The table the series sits on today — a current value, not a destination. */
  currentTableId: number;
  /**
   * Tables to offer. The page already holds `GET /tables`, which returns
   * **active tables only**, so this component neither fetches nor filters on
   * table state: if one is deactivated between that load and the click, the
   * API's "Table is inactive" is what staff read (admin#49 AC 2).
   */
  tables: TableEmbed[];
  /** Called after a successful write, so the page can re-read the series. */
  onTransferred: () => void;
}

/**
 * A dry run's plan, carrying the destination it was asked about.
 *
 * The id travels *with* the plan rather than being re-read from the picker,
 * so "what Apply will send" and "what the summary claims" are the same fact by
 * construction instead of by two lookups that a race can pull apart.
 */
interface Preview {
  tableId: number;
  result: SeriesTableTransferResult;
}

/**
 * Move a whole coach series to a different table (admin#49, api#85).
 *
 * Same two-step as `SeriesTimeShiftPanel`, and deliberately so — the endpoints
 * are siblings: `dry_run=true` first, which runs the identical server-side
 * planning code *without writing* and reports exactly which occurrences would
 * move and which it would leave alone; then the same body again, without the
 * flag, to commit.
 *
 * A transfer changes the table and nothing else. Occurrences keep their own
 * windows, which is why the preview lists each one's times rather than the
 * series' — a hand-re-timed occurrence travels along unchanged. Occurrences
 * that already started, that were cancelled, or that somebody moved to another
 * table individually stay where they are and come back in `skipped`.
 *
 * Unlike the time-shift panel this is offered on one-time sessions too: a
 * session booked onto the wrong table is exactly the mistake this endpoint
 * fixes, and `/table` has no one-time restriction. See
 * .ai/features/49-series-table-transfer/DECISIONS.md.
 */
export function SeriesTableTransferPanel({
  seriesId,
  currentTableId,
  tables,
  onTransferred,
}: Props) {
  const { push } = useToast();

  const [tableId, setTableId] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [conflict, setConflict] = useState<SeriesOccurrenceConflictDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Bumped by every request *and* every picker change, so a response arriving
   * late can tell whether it still answers the question on screen. Without it,
   * a dry run for Table 3 that lands after staff switched to Table 2 installs
   * Table 3's plan under a Table 2 headline — see `run`.
   */
  const requestSeq = useRef(0);

  const destinations = tables.filter((t) => t.id !== currentTableId);
  const nameOf = (id: number) => destinations.find((t) => t.id === id)?.name ?? `#${id}`;

  /**
   * A preview describes one specific destination. Changing the picker makes it
   * a description of something else, so it — and the errors that belong to it —
   * go with it. Without this, staff could preview a move to Table 3, switch to
   * Table 2, and press an Apply button that no longer matches what they saw.
   *
   * The sequence bump covers the same mistake made a moment earlier, while the
   * dry run is still open and there is no preview here to clear.
   */
  const changeTable = (next: string) => {
    requestSeq.current += 1;
    setTableId(next);
    setPreview(null);
    setConflict(null);
    setError(null);
  };

  const run = async (dryRun: boolean) => {
    const seq = (requestSeq.current += 1);
    // Apply sends the table the *preview* describes, never the picker's current
    // value. The two can only differ if something raced, and applying a
    // destination nobody previewed is precisely what this panel exists to
    // prevent — so the plan, not the control, is the source of truth.
    const targetId = dryRun ? Number(tableId) : preview!.tableId;
    setBusy(true);
    setConflict(null);
    setError(null);
    try {
      const body = { table_id: targetId };
      // The write path is the flag's *absence*, not `dry_run=false` — see the
      // wrapper. Two call sites rather than a computed options object so that
      // stays true at a glance.
      const result = dryRun
        ? await transferCoachSeriesTable(seriesId, body, { dryRun: true })
        : await transferCoachSeriesTable(seriesId, body);
      if (dryRun) {
        // Superseded while in flight: this plan describes a destination staff
        // have already moved on from. Drop it rather than render it.
        if (seq !== requestSeq.current) return;
        setPreview({ tableId: targetId, result });
        return;
      }
      // A successful write changed the server even if staff changed the picker
      // while it was in flight. Suppress stale copy, but always refetch.
      if (seq === requestSeq.current) {
        push(
          'success',
          `Series moved to ${nameOf(targetId)} · ` +
            `${result.moved.length} occurrence${result.moved.length === 1 ? '' : 's'} updated.`,
        );
      }
      setPreview(null);
      setTableId('');
      onTransferred();
    } catch (err) {
      if (seq !== requestSeq.current) return;
      const seriesConflict = seriesOccurrenceConflictDetail(err);
      if (seriesConflict) {
        setConflict(seriesConflict);
      } else {
        setError(err instanceof ApiError ? err.message : 'Table transfer failed');
      }
      // A rejected apply invalidates the preview it came from: the world
      // changed under it (someone took a target slot), so re-preview.
      setPreview(null);
    } finally {
      // Cleared even when superseded: the only thing that supersedes an
      // in-flight request is a picker change, which starts no request of its
      // own, so leaving `busy` set would strand the panel.
      setBusy(false);
    }
  };

  if (destinations.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={T.label}>Move to another table</span>
        <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
          No other active table to transfer to.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={T.label}>Move to another table</span>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="New table">
          <select
            aria-label="New table"
            value={tableId}
            onChange={(e) => changeTable(e.target.value)}
            style={selectStyle}
          >
            <option value="">—</option>
            {destinations.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button variant="secondary" onClick={() => void run(true)} disabled={busy || !tableId}>
          {busy && !preview ? 'Checking…' : 'Preview transfer'}
        </Button>
        {preview && (
          <>
            <Button onClick={() => void run(false)} disabled={busy}>
              {busy ? 'Transferring…' : 'Apply transfer'}
            </Button>
            <Button variant="secondary" onClick={() => setPreview(null)} disabled={busy}>
              Discard preview
            </Button>
          </>
        )}
      </div>

      <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
        Preview first — nothing is written until you apply. Times never change: every occurrence
        keeps its own window. Occurrences that have already started, that were cancelled, or that
        someone moved to another table individually stay where they are.
      </p>

      {error && (
        <div role="alert" style={{ ...T.caption, color: PS.errorText }}>
          {error}
        </div>
      )}

      {conflict && <SeriesOccurrenceConflictBanner detail={conflict} alternative="table" />}

      {preview && (
        <PreviewSummary result={preview.result} targetName={nameOf(preview.tableId)} />
      )}
    </div>
  );
}

function PreviewSummary({
  result,
  targetName,
}: {
  result: SeriesTableTransferResult;
  targetName: string;
}) {
  const { moved, skipped } = result;

  return (
    <div
      style={{
        border: `1px solid ${PS.borderLight}`,
        borderRadius: 3,
        padding: 12,
        background: PS.surfaceDisabled,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        // A long open-ended series previews many dates; scroll rather than
        // push the rest of the page off screen.
        maxHeight: 320,
        overflowY: 'auto',
      }}
    >
      <div style={{ ...T.subheading, color: PS.nearBlack }}>
        {moved.length} occurrence{moved.length === 1 ? '' : 's'} will move to {targetName}
      </div>
      {moved.length === 0 ? (
        <div style={{ ...T.caption, color: PS.textSec }}>
          Nothing to move — every upcoming occurrence stays where it is. Applying still moves the
          series itself, so occurrences materialized later land on {targetName}.
        </div>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {moved.map((m) => (
            <li key={m.booking_id} style={{ ...T.caption, color: PS.nearBlack }}>
              {/* Each move carries its own window — a transfer moves tables,
                  not times, so a hand-edited occurrence shows its own hours. */}
              <span style={dateCell}>{m.date}</span> · {formatClock(m.start_time)}–
              {formatClock(m.end_time)} <code>#{m.booking_id}</code>
            </li>
          ))}
        </ul>
      )}

      {skipped.length > 0 && (
        <>
          <div style={{ ...T.subheading, color: PS.nearBlack }}>
            {skipped.length} occurrence{skipped.length === 1 ? '' : 's'} stay
            {skipped.length === 1 ? 's' : ''} where {skipped.length === 1 ? 'it is' : 'they are'}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {skipped.map((s: SeriesOccurrenceSkip) => (
              <li key={s.booking_id} style={{ ...T.caption, color: PS.textSec }}>
                <span style={dateCell}>{s.date}</span> {formatClock(s.start_time)}–
                {formatClock(s.end_time)} · <span>{occurrenceSkipLabel(s.reason)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

const dateCell: CSSProperties = { fontFamily: "'Barlow',sans-serif" };

const selectStyle: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  background: PS.white,
};
